import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/components/AuthProvider';
import { apiClient } from '@/lib/api/client';
import {
  isFirstRunOnboardingAudience,
  isOnboardingCompletedLocally,
  cacheOnboardingCompleted,
  subscriptionProvesPriorSetup,
  isOnboardingCompletePending,
  markOnboardingComplete,
} from '@/lib/onboardingCompletion';

/**
 * THE ONE ANSWER TO "must this organization go through first-run onboarding?"
 *
 * 🔴 A WHATSAPP BUSINESS ACCOUNT IS NOT AN INPUT HERE, AND MUST NEVER BECOME ONE.
 * WeNext is workspace-based: a workspace is created during signup and a WABA is
 * never a prerequisite for onboarding, workspace creation, or using the app. The
 * ABSENCE of a business account says nothing about whether onboarding was done.
 * (Its PRESENCE is still positive evidence, but that is applied once, server-side,
 * by the self-healing read in `GET /onboarding/status` — not here.)
 *
 * Before this hook, `FirstRunOnboardingGate` redirected off /home whenever the org
 * had zero business accounts, regardless of `onboarding_completed`. Combined with
 * `OnboardingFirstRunGuard` — which lets a user with zero accounts STAY on
 * /onboarding — that pinned every completed-but-unconnected org out of the app:
 * /home ejected them and /onboarding accepted them, forever.
 *
 * ── Why this cannot loop ──────────────────────────────────────────────────────
 * Only two redirects exist, and they point in opposite directions:
 *   /home       → /onboarding   when this hook says 'required'
 *   /onboarding → /home         when the org HAS a business account
 * 'required' can only come from the authoritative status read, and that read
 * self-heals to completed whenever a business account exists
 * (`onboardingQARoutes.js` — the `existingAccount` branch). So "requirement is
 * required" and "a business account exists" are mutually exclusive states, and
 * the two redirects can never both be armed. The loop is structurally impossible
 * rather than prevented by matching hand-written conditions in two files.
 *
 * ── Why 'pending' is a distinct state ─────────────────────────────────────────
 * Never redirect on an unresolved answer. Callers must treat 'pending' as "do
 * nothing yet"; a boolean would force an unknown answer to masquerade as one of
 * the two real ones and would redirect users mid-flight.
 */
export type OnboardingRequirement = 'pending' | 'required' | 'complete';

export const ONBOARDING_STATUS_QUERY_KEY = 'onboarding-status';
const CURRENT_SUBSCRIPTION_KEY = ['current-subscription'] as const;

interface OnboardingStatusResponse {
  success?: boolean;
  onboarding_completed?: boolean;
  onboarding_completed_at?: string | null;
}

export function useOnboardingRequirement(): OnboardingRequirement {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const organizationId = (user as { organization_id?: string } | null)?.organization_id ?? null;

  /**
   * Audience check first: a non-owner/non-admin (an invited teammate joining an
   * existing organization, an agency, a super admin) is never routed through
   * first-run onboarding, so there is nothing to resolve and no request to make.
   */
  const isAudience = isFirstRunOnboardingAudience(user as never);

  /** Locally mirrored completion — org-scoped, survives logout, avoids a round trip. */
  const completedLocally = isOnboardingCompletedLocally(organizationId);

  /**
   * The mirror can be ahead of the server: `markOnboardingComplete` writes it
   * first and sets a pending flag if `POST /onboarding/complete` fails. Retry
   * here, or the DB flag is never written and the user is re-onboarded the next
   * time they sign in somewhere that has no local mirror.
   */
  useEffect(() => {
    if (!organizationId || !completedLocally) return;
    if (!isOnboardingCompletePending()) return;
    void markOnboardingComplete(organizationId);
  }, [organizationId, completedLocally]);

  /**
   * A real (non-signup-trial) subscription proves the org was set up before.
   * Read from cache only — never fetched here, so this can't delay the answer.
   */
  const cachedSubscription = queryClient.getQueryData(CURRENT_SUBSCRIPTION_KEY);
  const provenBySubscription = subscriptionProvesPriorSetup(cachedSubscription as never);

  const shortCircuited = !user || !isAudience || completedLocally || provenBySubscription;

  const { data, isSuccess, isError } = useQuery({
    // Organization-scoped: switching workspaces never changes the organization
    // (`GET /workspaces` lists workspaces for the caller's org), so a workspace
    // switch correctly reuses this answer while a different org gets its own.
    queryKey: [ONBOARDING_STATUS_QUERY_KEY, organizationId],
    queryFn: async (): Promise<boolean> => {
      const response = await apiClient.get<OnboardingStatusResponse>('/onboarding/status');
      if (response.error) throw new Error(response.error);
      const completed = response.data?.onboarding_completed === true;
      if (completed) cacheOnboardingCompleted(organizationId);
      return completed;
    },
    enabled: !shortCircuited && !!organizationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (!user) return 'pending';
  if (!isAudience) return 'complete';
  if (completedLocally || provenBySubscription) return 'complete';

  if (isSuccess) return data ? 'complete' : 'required';

  /**
   * Fail SAFE, not open: if the status read fails we must not eject a user who
   * has completed onboarding, and we must not trap a new one either. 'pending'
   * leaves both guards inert, so the user keeps whichever page they are on.
   */
  if (isError) return 'pending';

  return 'pending';
}

export default useOnboardingRequirement;
